import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { isUndefined } from 'util';

@Injectable({
  providedIn: 'root'
})
export class TestGroupsService {
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };
  private formOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    })
  };

  constructor(private http: HttpClient) { }

  getDeviceGroupDetails(appId: string): Observable<any> {
    const endpoint = '/dashboard/getdevicegroupsdetail';
    const options = {
      headers: new HttpHeaders().set('dashboard-version', '3.0'),
      params: new HttpParams().set('app_id', appId)
    };
    return this.http.get<any>(endpoint, { ...this.httpOptions, ...options }).pipe(
      map(this.extractData),
      catchError(this.handleError<any>('getDeviceGroupDetails'))
    );
  }

  createDeviceGroup(appId: string, deviceGroupName: string) {
    const endpoint = '/dashboard/createdevicegroup';

    const toEncode = {
      'app_id': appId,
      'device_group_name': deviceGroupName
    };

    return this.http.post<any>(endpoint, this.serializer(toEncode), this.formOptions).pipe(
      map(this.extractData),
      catchError(this.handleError<any>('createDeviceGroup'))
    );
  }

  deleteDeviceGroup(deviceGroupId: string, deleteDevices: boolean) {
    const endpoint = '/dashboard/deletedevicegroup';

    const toEncode = {
      'device_group_id': deviceGroupId,
      'delete_devices': deleteDevices ? 'True' : 'False'
    };

    return this.http.post<any>(endpoint, this.serializer(toEncode), this.formOptions).pipe(
      map(this.extractData),
      catchError(this.handleError<any>('createDeviceGroup'))
    );
  }

  /**
   * API: create a new test user 'be'
   * @param payload data to send
   */
  createDeviceNode(payload: any) {
    const endpoint = '/dashboard/createdevice';

    const toEncode = {
      'app_id': payload.appId,
      'device_id': payload.deviceId,
      'device_name': payload.deviceName,
      'device_group_id': payload.deviceGroupId
    };

    return this.http.post<any>(endpoint, this.serializer(toEncode), this.formOptions).pipe(
      map(this.extractData),
      catchError(this.handleError<any>('createDeviceNode'))
    );
  }

  /**
   * API: create a new test device 'web' 'ios' 'mobile'
   * @param payload data to send
   */
  createDeviceWebMobile(payload: any) {
    const endpoint = '/dashboard/testdevice/genurl';
    const data: any = {
      'app_id': payload.appId,
      'device_detail_name': payload.deviceName,
      'device_group_id': payload.deviceGroupId
    };
    if (!isUndefined(payload.webAppUrl)) {
      data.test_app_url = payload.webAppUrl;
    }
    return this.http.post<any>(endpoint, data, this.httpOptions).pipe(
      map(this.extractData),
      catchError(this.handleError<any>('createDeviceWebMobile'))
    );
  }

  /**
   * API: resend pairing URL
   * @param payload data to send
   */
  resendDevicePairingUrl(payload: any) {
    const endpoint = '/dashboard/testdevice/genurl?resend=true';
    const data: any = {
      'app_id': payload.appId,
      'device_detail_name': payload.deviceName,
      'device_group_id': payload.deviceGroupId
    };
    // resend web only
    if (!isUndefined(payload.webAppUrl)) {
      data.test_app_url = payload.webAppUrl;
    }
    return this.http.post<any>(endpoint, data, this.httpOptions).pipe(
      map(this.extractData),
      catchError(this.handleError<any>('resendDevicePairingUrl'))
    );
  }

  /**
   * API: delete a test device
   * @param deviceId id of the device to delete
   */
  deleteDevice(deviceId: string) {
    const endpoint = '/dashboard/deletedevice';

    const toEncode = {
      device_detail_id: deviceId
    };

    return this.http.post<any>(endpoint, this.serializer(toEncode), this.formOptions).pipe(
      map(this.extractData),
      catchError(this.handleError<any>('deleteDevice'))
    );
  }

  private serializer(JSONobj: any) {
    const serial: any = [];
    for (const key in JSONobj) {
      if (JSONobj.hasOwnProperty(key)) {
        serial.push(key + '=' + JSONobj[key]);
      }
    }

    return serial.join('&');
  }

  private extractData(res: Response) {
    const body = res;
    return body || {};
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure

      // TODO: better job of transforming error for user consumption

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
